/**
 * Custom game event constants.
 * Ported from the Phaser codebase — same string keys for consistency.
 * Using string constants prevents typos and enables autocomplete.
 */
export const GameEvents = {
  // Puzzle events
  PUZZLE_STARTED: 'puzzle:started',
  PUZZLE_PROGRESSED: 'puzzle:progressed',
  PUZZLE_SOLVED: 'puzzle:solved',
  // Phase 5 light-fail: emitted when a puzzle fails recoverably (harness
  // integrity hit zero, or an escort convoy was irrecoverably lost).
  // BaseLevel reloads the last checkpoint; HUD shows a fail toast. NO
  // game-over / permadeath.
  PUZZLE_FAILED: 'puzzle:failed',

  // Ability events
  ABILITY_ACTIVATED: 'ability:activated',
  ABILITY_DEACTIVATED: 'ability:deactivated',
  ABILITY_BLOCKED: 'ability:blocked',

  // Player events
  PLAYER_SWITCHED: 'player:switched',
  PLAYER_LANDED: 'player:landed',
  PLAYER_JUMPED: 'player:jumped',
  PLAYER_FOOTSTEP: 'player:footstep',
  /**
   * Emitted when the kill-plane respawn relocates a cat back to its
   * level spawn. QA navigators consume this as a fast-fail signal:
   * a respawn during a navigate means the planned path led the cat
   * into a void, and the navigator should bail with a clear reason
   * rather than transparently continue from the respawned position.
   */
  PLAYER_RESPAWNED: 'player:respawned',
  /**
   * Emitted whenever `clampToSeparation` actively damps a cat's
   * outward velocity because the pair exceeded MAX_SEPARATION. QA
   * traces use this as a telemetry signal — a navigator trying to
   * walk toward a target while this event fires means the camera
   * tether is fighting the move. Includes both cat positions + the
   * applied damping coefficient.
   */
  PLAYER_SEPARATION_CLAMPED: 'player:separationClamped',

  // Co-op events
  COOP_ABILITY_UNLOCKED: 'coop:abilityUnlocked',
  COOP_ABILITY_USED: 'coop:abilityUsed',

  // World events
  DAY_NIGHT_CHANGED: 'daynight:changed',
  NPC_CALMED: 'npc:calmed',
  PURR_PULSE: 'purr:pulse',
  ELDER_ENCOUNTER: 'elder:encounter',

  // NPC events
  NPC_STATE_CHANGED: 'npc:stateChanged',
  KITTEN_PENNED: 'kitten:penned',
  FOOD_DELIVERED: 'food:delivered',
  FOOD_PICKED_UP: 'food:pickedUp',
  CROW_FED: 'crow:fed',
  OPOSSUM_FOLLOWING: 'opossum:following',
  NEST_CAPTURED: 'nest:captured',

  // Cutscene events
  CUTSCENE_STARTED: 'cutscene:started',
  CUTSCENE_STEP: 'cutscene:step',
  CUTSCENE_ENDED: 'cutscene:ended',

  // Scene transition events
  TRANSITION_START: 'transition:start',
  TRANSITION_COMPLETE: 'transition:complete',

  // Level events
  LEVEL_COMPLETE: 'level:complete',
  LEVEL_STARTED: 'level:started',
  LEVEL_CRASHED: 'level:crashed',

  // Checkpoint / flow (Phase 5). CHECKPOINT_SAVED fires after BaseLevel
  // snapshots a v2 CheckpointData on each distinct puzzle solve. Respawn /
  // restore listeners read the persisted data via SaveSystem.getCheckpoint.
  // CHECKPOINT_RESTORED fires when a level (re)entry rehydrates the last
  // checkpoint at load time, before any QA navigate runs.
  CHECKPOINT_SAVED: 'checkpoint:saved',
  CHECKPOINT_RESTORED: 'checkpoint:restored',
  // Fired by in-level checkpoint markers (e.g. the Conservatory escort's
  // glowing waypoint spheres) when a cat first touches one. BaseLevel's
  // checkpoint capture listens and snapshots a mid-puzzle CheckpointData
  // so respawns return the player to the marker instead of the last
  // puzzle-solve position.
  CHECKPOINT_TOUCHED: 'checkpoint:touched',

  // Resonant Drop
  RESONANT_DROP_TRIGGERED: 'resonantDrop:triggered',
  BLOCKAGE_SHATTERED: 'blockage:shattered',
  // Fired specifically when a Resonant Drop shockwave is confirmed to
  // have shattered a blockage. Distinct from BLOCKAGE_SHATTERED (which
  // can also fire from scripted / cutscene paths) so QA telemetry can
  // correlate the co-op ability use with its payoff.
  SHOCKWAVE_SHATTERED: 'shockwave:shattered',

  // Chronos Bubble
  CHRONOS_BUBBLE_TRIGGERED: 'chronosBubble:triggered',
  CHRONOS_BUBBLE_ENDED: 'chronosBubble:ended',

  // Day/Night (legacy — replaced by CONSERVATORY_CYCLE_* but kept for one
  // release so the old DayNightTransition puzzle + Fox startle wiring
  // keep working while the Conservatory rebuild lands. Delete after
  // MasterValvePuzzle ships and all StealthEscort callers are migrated.)
  DAY_NIGHT_TRANSITION: 'daynight:transition',

  // Conservatory cycle (canonical per 2026-04-14 companion book §4.2).
  // `CYCLE_REQUESTED` fires when a ValveCrank TTT sequence completes;
  // `CYCLE_TRANSITION` fires when the cycle actually starts lerping
  // (after hysteresis check passes); `PHASE_CHANGED` fires when the
  // lerp settles. The rejected flag on CYCLE_REQUESTED lets the HUD
  // toast a "regulator stabilizing" message without the cycle having
  // to re-emit.
  CONSERVATORY_CYCLE_REQUESTED: 'conservatoryCycle:requested',
  CONSERVATORY_CYCLE_TRANSITION: 'conservatoryCycle:transition',
  CONSERVATORY_PHASE_CHANGED: 'conservatoryCycle:phaseChanged',

  // Boiler-Bound Sundew apex
  SUNDEW_VALVE_ACTIVATED: 'sundew:valveActivated',
  // Per-hit milestone — fires on every successful TTT rhythm hit on a
  // sundew valve so telemetry can see the ramp to activation, not just
  // the moment the valve latches. Also mirrors the cycle-request payload
  // for the incubation/irrigation variants.
  SUNDEW_VALVE_HIT: 'sundew:valveHit',
  SUNDEW_HEAT_PULSE: 'sundew:heatPulse',
  SUNDEW_TONGUE_SWEEP: 'sundew:tongueSweep',
  SUNDEW_NODE_CLEARED: 'sundew:nodeCleared',
  SUNDEW_PHASE_ADVANCED: 'sundew:phaseAdvanced',
  SUNDEW_DASH_ENTERED: 'sundew:dashEntered',
  // Fired when the dash-zone opens so VFX + audio can stage the canonical
  // "iron jaws" framing (book p.20-21 — tongue-maw imagery warning the
  // player that the mouth is live). Armed=false fires when the zone
  // clears (dash sync resolved or apex phase advanced).
  SUNDEW_IRON_JAWS_ARMED: 'sundew:ironJawsArmed',
  // Tick for Phase 3B internal Purr hold progress — used by HUD + VFX to
  // render the 0→1 ramp as Minna holds `F` inside the internal chamber.
  // Fires every update frame while the hold is non-zero, plus a final
  // tick at `progress=1` when the hold completes.
  SUNDEW_INTERNAL_PURR_PROGRESS: 'sundew:internalPurrProgress',
  OPOSSUM_FROZEN: 'opossum:frozen',
  PLAYER_DAMAGED: 'player:damaged',

  // Overgrown Conservatory NPCs
  FOX_ALERT: 'fox:alert',
  FOX_HUNTING: 'fox:hunting',
  // Canon PR 3: fired when a cycle transition freezes the fox for its
  // startle window. Puzzle code uses this to dampen the alert-light loop
  // and let audio play a "yip" SFX without stepping on the hunting music.
  FOX_STARTLED: 'fox:startled',
  OPOSSUM_HIDDEN: 'opossum:hidden',
  OPOSSUM_SCATTERED: 'opossum:scattered',

  // Guardian Dog (Liminal Atrium, canon p.7 sleeping-dog concurrent puzzle)
  // Emits on every state transition plus a path-blocked pulse so the HUD /
  // tutorial code can surface the "run to the gramophone" prompt without
  // having to poll the NPC's internal state machine.
  GUARDIAN_DOG_STIRRED: 'guardianDog:stirred',
  GUARDIAN_DOG_BLOCKED_PATH: 'guardianDog:blockedPath',
  GUARDIAN_DOG_SETTLED: 'guardianDog:settled',

  // Gramophone (Liminal Atrium, Basil-operated sleepy-melody interactive)
  GRAMOPHONE_PLAYED: 'gramophone:played',
  GRAMOPHONE_STOPPED: 'gramophone:stopped',

  // Crane lever events
  CRANE_LEVER_ALIGNED: 'craneLever:aligned',
  CRANE_LEVER_MISALIGNED: 'craneLever:misaligned',
  CRANE_LEVER_LOCKED: 'craneLever:locked',
  CRANE_LEVER_UNLOCKED: 'craneLever:unlocked',

  // Crow events
  CROW_DROPPED_ITEM: 'crow:droppedItem',

  // Jigsaw board events
  JIGSAW_BOARD_SOLVED: 'jigsawBoard:solved',

  // Hoodie Papoose — post-apex carry mechanic (canon book p.13).
  // `READY` fires when the Jigsaw solves and Minna actually wears the
  // papoose (prop attached to her mesh). `KITTEN_LOADED` / `UNLOADED`
  // fire when a calmed kitten is attached to / detached from Minna's
  // back so the pen and HUD can react without polling carrier state.
  HOODIE_PAPOOSE_READY: 'hoodiePapoose:ready',
  HOODIE_PAPOOSE_KITTEN_LOADED: 'hoodiePapoose:kittenLoaded',
  HOODIE_PAPOOSE_KITTEN_UNLOADED: 'hoodiePapoose:kittenUnloaded',

  // Light Path (L3) lens events — currently drive only HUD toasts, but
  // QA telemetry needs to see the actual pickup/place moments to
  // reconstruct a solve timeline.
  LENS_PICKED_UP: 'lens:pickedUp',
  LENS_PLACED: 'lens:placed',

  // Shadow Slip events
  SHADOW_SLIP_ENTERED: 'shadowSlip:entered',
  SHADOW_SLIP_EXITED: 'shadowSlip:exited',

  // Turn-Table Touch events
  TURN_TABLE_TOUCH_HIT: 'turnTableTouch:hit',
  TURN_TABLE_TOUCH_MISS: 'turnTableTouch:miss',

  // Interactive object events
  PRESSURE_PLATE_ACTIVATED: 'pressurePlate:activated',
  PRESSURE_PLATE_DEACTIVATED: 'pressurePlate:deactivated',
  DIAL_TURNED: 'dial:turned',
  DIAL_ALIGNED: 'dial:aligned',
  DIAL_LOCKED: 'dial:locked',
  DIAL_UNLOCKED: 'dial:unlocked',
  CRYSTAL_CHARGED: 'crystal:charged',
  CRYSTAL_ACTIVATED: 'crystal:activated',
  PORTAL_OPENED: 'portal:opened',
  PORTAL_ENTERED: 'portal:entered',
  INTERACTIVE_REVEALED: 'interactive:revealed',
  INTERACTIVE_CONCEALED: 'interactive:concealed',
  INTERACTIVE_SOLVED: 'interactive:solved',

  // Overclocked Leap
  OVERCLOCKED_LEAP_CHARGING: 'overclockedLeap:charging',
  OVERCLOCKED_LEAP_FIRED: 'overclockedLeap:fired',

  // HUD
  HUD_SHOW_PROMPT: 'hud:showPrompt',
  HUD_HIDE_PROMPT: 'hud:hidePrompt',
  HUD_TOAST: 'hud:toast',
  HUD_GLYPHS_CHANGED: 'hud:glyphsChanged',
  /** Always-on objective line (not gated by hintsEnabled). Payload: { text: string }. Empty string clears. */
  HUD_SET_OBJECTIVE: 'hud:setObjective',
  /**
   * Tutorial cue chip (ui/tutorialCues.ts). slot "objective" is pinned
   * above `anchor` in the world; slot "controls" sits at the bottom edge.
   * `cue: null` clears the slot. Gated by the hints toggle.
   */
  HUD_SET_CUE: 'hud:setCue',

  // Synergy
  SYNERGY_ENTERED: 'synergy:entered',
  SYNERGY_EXITED: 'synergy:exited',

  // Energy system
  ENERGY_CHANGED: 'energy:changed',
  ENERGY_DEPLETED: 'energy:depleted',
  ENERGY_RESTORED: 'energy:restored',
  COREGULATION_ACTIVE: 'coregulation:active',
  COREGULATION_ENDED: 'coregulation:ended',

  // Harness wound system (Basil — composes WITH EnergySystem).
  // `HARNESS_STATE_CHANGED` fires on threshold crossings only;
  // `HARNESS_INTEGRITY_CHANGED` fires every time integrity moves so the
  // HUD bar can smoothly animate. `HARNESS_DAMAGED` / `HARNESS_HEALED`
  // are one-shot edge events used by VFX, audio, and banter.
  HARNESS_STATE_CHANGED: 'harness:stateChanged',
  HARNESS_INTEGRITY_CHANGED: 'harness:integrityChanged',
  HARNESS_DAMAGED: 'harness:damaged',
  HARNESS_HEALED: 'harness:healed',
  GREASE_CACHE_CONSUMED: 'greaseCache:consumed',

  // Menu / Pause
  MENU_START_PRESSED: 'menu:startPressed',
  MENU_LEVEL_SELECTED: 'menu:levelSelected',
  MENU_BACK_PRESSED: 'menu:backPressed',
  GAME_PAUSED: 'game:paused',
  GAME_RESUMED: 'game:resumed',
  PAUSE_RESTART_LEVEL: 'pause:restartLevel',
  PAUSE_RESTART_FROM_CHECKPOINT: 'pause:restartFromCheckpoint',
  PAUSE_QUIT_TO_MENU: 'pause:quitToMenu',

  // Input mode
  INPUT_MODE_CHANGED: 'input:mode-changed',

  // Accessibility
  ACCESSIBILITY_CHANGED: 'a11y:changed',

  // Settings
  VOLUME_CHANGED: 'settings:volumeChanged',

  // Making Biscuits (Minna-only kneading mini-game, Elder lore anchors)
  // Canon: `docs/specs/SPEC-making-biscuits.md`. Biscuits fires when Minna
  // activates an ElderBiscuitAnchor; each alternating A/D tap emits BEAT;
  // reaching trance-depth 100 emits COMPLETED which drives VISION_*.
  BISCUITS_STARTED: 'biscuits:started',
  BISCUITS_BEAT: 'biscuits:beat',
  BISCUITS_COMPLETED: 'biscuits:completed',
  BISCUITS_CANCELLED: 'biscuits:cancelled',

  // Vision cutscene (post-biscuits Elder memory playback).
  VISION_STARTED: 'vision:started',
  VISION_BEAT: 'vision:beat',
  VISION_ENDED: 'vision:ended',
} as const;

// ---------------------------------------------------------------------------
// Typed payload map — maps each event key to its payload type.
//
// Events with `void` payload take no arguments.
// The EventBus uses this to provide compile-time type checking at emit/on sites.
// Events NOT in this map still work via the fallback signature (backward compat).
// ---------------------------------------------------------------------------

import type { TransitionConfig } from "../systems/SceneTransition";
import type { CutsceneStep } from "../systems/CutsceneSystem";

/** Position payload reused by several ability events. */
export interface PositionPayload {
  x: number;
  y: number;
  z: number;
}

export interface EventPayloadMap {
  // Puzzle events
  [GameEvents.PUZZLE_STARTED]: { puzzleId: string };
  [GameEvents.PUZZLE_PROGRESSED]: { puzzleId?: string; current?: number; total?: number; step?: string };
  [GameEvents.PUZZLE_SOLVED]: { puzzleId?: string };
  [GameEvents.PUZZLE_FAILED]: { puzzleId: string; reason: 'harness' | 'escort' };

  // Ability events
  [GameEvents.ABILITY_ACTIVATED]: { ownerId: string; abilityId: string; abilityName: string };
  [GameEvents.ABILITY_DEACTIVATED]: { ownerId: string; abilityId: string; abilityName: string };
  [GameEvents.ABILITY_BLOCKED]: { ownerId: string; abilityId: string; reason: 'cooldown' | 'energy' | 'range' | 'locked' | 'grounded' };

  // Player events
  [GameEvents.PLAYER_SWITCHED]: 1 | 2;
  [GameEvents.PLAYER_LANDED]: string;
  [GameEvents.PLAYER_JUMPED]: string;
  [GameEvents.PLAYER_FOOTSTEP]: { cat: 'minna' | 'basil' };
  [GameEvents.PLAYER_SEPARATION_CLAMPED]: {
    cat: "minna" | "basil";
    separation: number;
    damping: number;
    selfX: number;
    partnerX: number;
  };
  [GameEvents.PLAYER_RESPAWNED]: {
    cat: "minna" | "basil";
    // Kernel-shaped companions (Phase 5). `cat` stays canonical because the
    // QA CatNavigator reads `payload.cat` (CatNavigator.ts:357); these are
    // additive so existing emitters/consumers keep compiling.
    characterId?: "minna" | "basil";
    x?: number;
    y?: number;
    z?: number;
    reason?: "fall" | "fail" | "restore";
  };

  // Co-op events
  [GameEvents.COOP_ABILITY_UNLOCKED]: { abilityId: string; abilityName?: string };
  [GameEvents.COOP_ABILITY_USED]: { abilityId: string };

  // World events
  [GameEvents.DAY_NIGHT_CHANGED]: { phase: string; isNight: boolean };
  [GameEvents.NPC_CALMED]: { npcKey: string; species?: string };
  [GameEvents.PURR_PULSE]: { sourcePosition: { x: number; y: number; z: number }; radius: number };
  [GameEvents.ELDER_ENCOUNTER]: { elder: string; level?: string };

  // NPC events
  [GameEvents.NPC_STATE_CHANGED]: { npcKey: string; prevState: string; newState: string };
  [GameEvents.KITTEN_PENNED]: { npcKey?: string; key?: string; count?: number; required?: number };
  // Canon p.13: food has a `kind` (mash / seeds / walnut). The Crow
  // declares a per-run stochastic preference — delivering the matching
  // `kind` double-counts (see `CrowSquirrelPuzzle`). Older listeners
  // that only care about `foodKey` remain source-compatible because the
  // new field is optional.
  [GameEvents.FOOD_DELIVERED]: { foodKey: string; kind?: "mash" | "seeds" | "walnut" };
  [GameEvents.FOOD_PICKED_UP]: { foodKey: string; kind?: "mash" | "seeds" | "walnut" };
  [GameEvents.CROW_FED]: { npcKey: string; foodCount: number };
  [GameEvents.OPOSSUM_FOLLOWING]: { npcKey: string };
  [GameEvents.NEST_CAPTURED]: { nestKey: string; opossumKey?: string };

  // Cutscene events
  [GameEvents.CUTSCENE_STARTED]: void;
  [GameEvents.CUTSCENE_STEP]: { index: number; step: CutsceneStep };
  [GameEvents.CUTSCENE_ENDED]: void;

  // Scene transition events
  [GameEvents.TRANSITION_START]: TransitionConfig;
  [GameEvents.TRANSITION_COMPLETE]: { targetScene: string };

  // Level events
  [GameEvents.LEVEL_COMPLETE]: { level: string };
  [GameEvents.LEVEL_STARTED]: string;
  [GameEvents.LEVEL_CRASHED]: { levelKey: string; errorMessage: string };

  // Checkpoint / flow (Phase 5)
  [GameEvents.CHECKPOINT_SAVED]: { levelId: string; puzzleIndex: number };
  [GameEvents.CHECKPOINT_RESTORED]: { levelId: string; puzzleIndex: number };
  [GameEvents.CHECKPOINT_TOUCHED]: { checkpointKey: string };

  // Resonant Drop
  [GameEvents.RESONANT_DROP_TRIGGERED]: PositionPayload & { radius: number };
  [GameEvents.BLOCKAGE_SHATTERED]: { key?: string };
  [GameEvents.SHOCKWAVE_SHATTERED]: PositionPayload & { radius: number; blockageKey?: string };

  // Chronos Bubble
  [GameEvents.CHRONOS_BUBBLE_TRIGGERED]: PositionPayload & { radius: number; timeScale: number };
  [GameEvents.CHRONOS_BUBBLE_ENDED]: void;

  // Day/Night (legacy)
  [GameEvents.DAY_NIGHT_TRANSITION]: { from: string; to: string };

  // Conservatory cycle (canonical)
  [GameEvents.CONSERVATORY_CYCLE_REQUESTED]: {
    target: "incubation" | "irrigation";
    accepted: boolean;
    /** Reason string when rejected: "locked" | "same" | "transitioning". */
    reason?: string;
    /** Which ValveCrank fired the request (for HUD feedback). */
    sourceKey?: string;
  };
  [GameEvents.CONSERVATORY_CYCLE_TRANSITION]: {
    from: "incubation" | "irrigation";
    to: "incubation" | "irrigation";
    durationMs: number;
  };
  [GameEvents.CONSERVATORY_PHASE_CHANGED]: {
    phase: "incubation" | "irrigation";
    isIncubation: boolean;
  };

  // Boiler-Bound Sundew apex
  [GameEvents.SUNDEW_VALVE_ACTIVATED]: { key: string };
  [GameEvents.SUNDEW_VALVE_HIT]: {
    key: string;
    currentHits: number;
    required: number;
    primed: boolean;
  };
  [GameEvents.SUNDEW_HEAT_PULSE]: {
    origin: { x: number; y: number; z: number };
    radius: number;
  };
  [GameEvents.SUNDEW_TONGUE_SWEEP]: {
    fromX: number;
    toX: number;
    durationMs: number;
  };
  [GameEvents.SUNDEW_NODE_CLEARED]: { nodeKey: string; remaining: number };
  // Phase advance milestones. Canon Phase 3B ("internal Purr hold", book
  // p.20-21 + SPEC §5.3 "unwiring from the inside") fires as phase 3.5
  // between the dash sync (phase 3) and the final release (phase 4).
  [GameEvents.SUNDEW_PHASE_ADVANCED]: { phase: 1 | 2 | 3 | 3.5 | 4 };
  [GameEvents.SUNDEW_DASH_ENTERED]: { cat: "minna" | "basil"; timeMs: number };
  [GameEvents.SUNDEW_IRON_JAWS_ARMED]: {
    /** True while the jaws are a risk-zone the cats must dash into. */
    armed: boolean;
    center: { x: number; y: number; z: number };
    /** Sphere radius of the iron-jaws trigger zone. */
    radius: number;
  };
  [GameEvents.SUNDEW_INTERNAL_PURR_PROGRESS]: {
    /** Minna's current Purr-hold progress [0..1] inside the chamber. */
    progress: number;
    /** Total hold required in ms (canonical 6000). */
    requiredMs: number;
    /** Time held so far in ms. */
    heldMs: number;
  };
  [GameEvents.OPOSSUM_FROZEN]: { npcKey: string };
  [GameEvents.PLAYER_DAMAGED]: {
    cat: "minna" | "basil";
    source: string;
    severity: "light" | "heavy";
  };

  // Overgrown Conservatory NPCs
  [GameEvents.FOX_ALERT]: { foxKey: string };
  [GameEvents.FOX_HUNTING]: { foxKey: string };
  [GameEvents.FOX_STARTLED]: { foxKey: string };
  [GameEvents.OPOSSUM_HIDDEN]: { npcKey: string };
  [GameEvents.OPOSSUM_SCATTERED]: { npcKey: string };

  // Guardian Dog (canon p.7 "concurrent puzzle" thesis beat)
  [GameEvents.GUARDIAN_DOG_STIRRED]: {
    npcKey: string;
    /** Who / what woke the dog (player or interactive key). */
    source: "minna" | "basil" | string;
    /** World position where the trigger was detected. */
    at: { x: number; y: number };
  };
  [GameEvents.GUARDIAN_DOG_BLOCKED_PATH]: {
    npcKey: string;
    /** Message hint for the HUD — suppressed when empty. */
    message?: string;
  };
  [GameEvents.GUARDIAN_DOG_SETTLED]: {
    npcKey: string;
    /** Which mechanism resettled the dog — "gramophone" | "purr" | "timeout". */
    reason: string;
  };

  // Gramophone
  [GameEvents.GRAMOPHONE_PLAYED]: {
    key: string;
    /** World position of the gramophone root at the moment it was played. */
    at: { x: number; y: number; z: number };
    /** How long the lullaby plays for (seconds). */
    durationSeconds: number;
  };
  [GameEvents.GRAMOPHONE_STOPPED]: { key: string };

  // Crane lever events — keyed by the interactive's string key
  [GameEvents.CRANE_LEVER_ALIGNED]: string;
  [GameEvents.CRANE_LEVER_MISALIGNED]: string;
  [GameEvents.CRANE_LEVER_LOCKED]: string;
  [GameEvents.CRANE_LEVER_UNLOCKED]: string;

  // Crow events
  [GameEvents.CROW_DROPPED_ITEM]: { npcKey: string; x: number; y: number };

  // Jigsaw board events — keyed by the interactive's string key
  [GameEvents.JIGSAW_BOARD_SOLVED]: string;

  // Hoodie Papoose — payload mirrors the kitten's npcKey so the pen can
  // dedupe with its own KITTEN_PENNED stream.
  [GameEvents.HOODIE_PAPOOSE_READY]: void;
  [GameEvents.HOODIE_PAPOOSE_KITTEN_LOADED]: { npcKey: string };
  [GameEvents.HOODIE_PAPOOSE_KITTEN_UNLOADED]: { npcKey: string; reason: "pen" | "manual" | "dispose" };

  // Light Path lens events (L3)
  [GameEvents.LENS_PICKED_UP]: { lensKey: string; carrier: string };
  [GameEvents.LENS_PLACED]: {
    lensKey: string;
    pedestalKey: string;
    placedCount: number;
    required: number;
  };

  // Shadow Slip events
  [GameEvents.SHADOW_SLIP_ENTERED]: void;
  [GameEvents.SHADOW_SLIP_EXITED]: void;

  // Turn-Table Touch events
  [GameEvents.TURN_TABLE_TOUCH_HIT]: { combo: number; targetPosition: { x: number; y: number; z: number } };
  [GameEvents.TURN_TABLE_TOUCH_MISS]: { targetPosition: { x: number; y: number; z: number } };

  // Interactive object events — all keyed by the interactive's string key
  [GameEvents.PRESSURE_PLATE_ACTIVATED]: string;
  [GameEvents.PRESSURE_PLATE_DEACTIVATED]: string;
  [GameEvents.DIAL_TURNED]: string;
  [GameEvents.DIAL_ALIGNED]: string;
  [GameEvents.DIAL_LOCKED]: string;
  [GameEvents.DIAL_UNLOCKED]: string;
  [GameEvents.CRYSTAL_CHARGED]: string;
  [GameEvents.CRYSTAL_ACTIVATED]: string;
  [GameEvents.PORTAL_OPENED]: string;
  [GameEvents.PORTAL_ENTERED]: string;
  [GameEvents.INTERACTIVE_REVEALED]: string;
  [GameEvents.INTERACTIVE_CONCEALED]: string;
  [GameEvents.INTERACTIVE_SOLVED]: string;

  // Overclocked Leap
  [GameEvents.OVERCLOCKED_LEAP_CHARGING]: { position: { x: number; y: number; z: number } };
  [GameEvents.OVERCLOCKED_LEAP_FIRED]: { position: { x: number; y: number; z: number }; mode: string };

  // HUD
  [GameEvents.HUD_SHOW_PROMPT]: { text: string };
  [GameEvents.HUD_HIDE_PROMPT]: void;
  [GameEvents.HUD_TOAST]: { message: string; color?: string };
  [GameEvents.HUD_GLYPHS_CHANGED]: { controllerActive: boolean };
  [GameEvents.HUD_SET_OBJECTIVE]: { text: string };
  [GameEvents.HUD_SET_CUE]: {
    slot: "objective" | "controls";
    /** A step cue, "controls" for the movement cue, or null to clear. */
    cue: import("../ui/tutorialCues").CueDef | "controls" | null;
    anchor?: { x: number; y: number; z: number };
  };

  // Synergy
  [GameEvents.SYNERGY_ENTERED]: void;
  [GameEvents.SYNERGY_EXITED]: void;

  // Energy system
  [GameEvents.ENERGY_CHANGED]: { character: string; current: number; max: number };
  [GameEvents.ENERGY_DEPLETED]: { character: string };
  [GameEvents.ENERGY_RESTORED]: { character: string };
  [GameEvents.COREGULATION_ACTIVE]: void;
  [GameEvents.COREGULATION_ENDED]: void;

  // Harness wound system — Basil only. See SPEC-wounds-and-medicine.md.
  [GameEvents.HARNESS_STATE_CHANGED]: {
    prev: "intact" | "cracked" | "venting" | "limping";
    next: "intact" | "cracked" | "venting" | "limping";
    /** Why the transition fired: damage source, heal source, init, etc. */
    reason: string;
    integrity: number;
  };
  [GameEvents.HARNESS_INTEGRITY_CHANGED]: {
    current: number;
    max: number;
    state: "intact" | "cracked" | "venting" | "limping";
  };
  [GameEvents.HARNESS_DAMAGED]: {
    amount: number;
    source: string;
    integrity: number;
    state: "intact" | "cracked" | "venting" | "limping";
  };
  [GameEvents.HARNESS_HEALED]: {
    amount: number;
    source: string;
    integrity: number;
    state: "intact" | "cracked" | "venting" | "limping";
  };
  [GameEvents.GREASE_CACHE_CONSUMED]: {
    key: string;
    position: { x: number; y: number; z: number };
    healAmount: number;
  };

  // Menu / Pause
  [GameEvents.MENU_START_PRESSED]: void;
  [GameEvents.MENU_LEVEL_SELECTED]: string;
  [GameEvents.MENU_BACK_PRESSED]: void;
  [GameEvents.GAME_PAUSED]: void;
  [GameEvents.GAME_RESUMED]: void;
  [GameEvents.PAUSE_RESTART_LEVEL]: void;
  [GameEvents.PAUSE_RESTART_FROM_CHECKPOINT]: void;
  [GameEvents.PAUSE_QUIT_TO_MENU]: void;

  // Input mode
  [GameEvents.INPUT_MODE_CHANGED]: { solo: boolean };

  // Accessibility — broadcast every time a setting changes so HUD,
  // post-process pipelines, and cutscene shake can re-evaluate.
  [GameEvents.ACCESSIBILITY_CHANGED]: {
    reducedMotion: boolean;
    colorblindMode: "none" | "deuteranopia" | "protanopia" | "tritanopia";
    fontScale: number;
    highContrastUI: boolean;
    autoKneadAssist?: boolean;
    beatWindowMultiplier?: number;
    dialogueAdvance?: "auto" | "manual";
    dialogueDwellMultiplier?: number;
  };

  // Settings
  [GameEvents.VOLUME_CHANGED]: { master: number; sfx: number; music: number };

  // Making Biscuits — payloads expose the anchor's owning Elder so HUD,
  // audio, and vision renderer can route per-Elder content from a single
  // subscription.
  [GameEvents.BISCUITS_STARTED]: {
    anchorKey: string;
    elder: "leo" | "gus" | "kitty";
    assistMode: boolean;
    /** World position of the anchor (UI overlays may decorate the scene). */
    position: { x: number; y: number; z: number };
  };
  [GameEvents.BISCUITS_BEAT]: {
    anchorKey: string;
    paw: "left" | "right";
    /** Quality of the beat: 'good' adds progress; 'soft' adds reduced progress. */
    quality: "good" | "soft";
    /** Trance depth after this beat [0..100]. */
    depth: number;
  };
  [GameEvents.BISCUITS_COMPLETED]: {
    anchorKey: string;
    elder: "leo" | "gus" | "kitty";
    replay: boolean;
  };
  [GameEvents.BISCUITS_CANCELLED]: {
    anchorKey: string;
    elder: "leo" | "gus" | "kitty";
    /** Reason the session ended early — "movement" | "cancel" | "dispose". */
    reason: string;
  };

  // Vision cutscene payloads. `elder` drives per-Elder audio cue routing;
  // `replay` is set on subsequent visits (shorter, echo-only playback).
  [GameEvents.VISION_STARTED]: {
    elder: "leo" | "gus" | "kitty";
    replay: boolean;
  };
  [GameEvents.VISION_BEAT]: {
    elder: "leo" | "gus" | "kitty";
    index: number;
    line?: string;
  };
  [GameEvents.VISION_ENDED]: {
    elder: "leo" | "gus" | "kitty";
  };
}
